import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ComplexRetrievalCallbacks, ComplexRetrievalRepository, RetrievalTimeCost } from '../../services/complex-retrieval-service';
import { SearchNoveltyApi } from '@/infrastructure/api-functions/core-drafting-api/search-novelty-api';
import { SearchNoveltyResultApi, type BaseSearchResult, type FeatureKeyword, type FinalResult, type IpcCpcSearch, type SearchNoveltyResultResponse, type SearchStrategy, type SemanticSearch } from '@/infrastructure/api-functions/core-drafting-api/search-novelty-result-api';
import { sleep } from '@telecom/shared/utils/task';
import {
    Parameter,
    type PatentProfileWithMatchedTechFeature,
    type PaperProfileWithMatchedTechFeature,
    type WebsiteProfileWithMatchedTechFeature,
    type RetrievalResult,
    type TechFeatureSynonym,
} from '../../types';
import { create, Logger } from '@patsnap/tahiti-core';
import { HUMAN_RETRIEVAL_PER_PATENT_TIME, RETRIEVAL_STRATEGY } from '../../constants';
import _ from 'lodash';
import { I18nService } from '@telecom/shared/services/i18n';
import {
    RetrievalProgressModelV2,
    type RetrievalProgressPaperResult,
    type RetrievalProgressWebsiteResult,
    type RetrievalProgressFinalResult,
} from '../../models/retrieval-progress-model-v2';
import { HeaderService } from '@telecom/shared/services/header-service/header-service';
import { buildLiteratureUrlWithRegion } from '@/presentation/pages/novelty-check-report/composables/use-literature-url';

/**
 * 单个检索策略执行状态
 */
const RETRIEVAL_STATUS = {
    /**
     * 运行中
     */
    RUNNING: 'running',
    /**
     * 成功
     */
    SUCCESS: 'success',
};

/**
 * 整体任务状态
 */
const TASK_STATUS = {
    SUCCESS: 'success',
    RUNNING: 'running',
};

/**
 * 再次检索的延迟时间
 */
const SEARCH_DELAY = 1000;

/**
 * 空检索结果
 * @description 用于表示检索结果为空
 */
export const EmptyRetrievalResult: RetrievalResult = Object.freeze({
    idea: '',
    searchExpress: '',
    patents: [],
    strategyParameters: [],
    nextPlan: '',
    finished: false,
});

@injectable()
export class ComplexRetrievalRepositoryImpl implements ComplexRetrievalRepository {
    // eslint-disable-next-line max-params
    constructor(
        @inject(SearchNoveltyApi)
        private readonly _searchNoveltyApi: SearchNoveltyApi,
        @inject(SearchNoveltyResultApi)
        private readonly _searchNoveltyResultApi: SearchNoveltyResultApi,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(RetrievalProgressModelV2)
        private readonly _retrievalProgressModel: RetrievalProgressModelV2,
        @inject(HeaderService)
        private readonly _headerService: HeaderService,
    ) { }

    terminate(): Promise<void> {
        this._abortController?.abort();
        return Promise.resolve();
    }

    private _abortController: AbortController | null = null;

    async search(taskId: string, callbacks: ComplexRetrievalCallbacks): Promise<void> {
        callbacks.onRetrievalRunning(RETRIEVAL_STRATEGY.SEMANTIC_SEARCH, true);
        callbacks.onRetrievalSuccess(RETRIEVAL_STRATEGY.SEMANTIC_SEARCH, EmptyRetrievalResult);
        await this._searchNoveltyApi.startSearchNovelty({
            task_id: taskId,
        });
        return this._loopSearchNoveltyResult(taskId, callbacks);
    }

    private _validateFeatureKeyword(data: FeatureKeyword): boolean {
        return !!data.tech_feature
            && !!data.synonym
            && !!data.synonym_score
            && !!data.translation
            && Array.isArray(data.words);
    }

    private _convertToTechFeatureSynonym(data: FeatureKeyword[]): TechFeatureSynonym[] {
        return data
            .filter((item) => {
                return this._validateFeatureKeyword(item);
            })
            .map((item) => {
                return {
                    techFeature: item.tech_feature,
                    synonym: item.synonym,
                    synonymScore: item.synonym_score,
                    translation: item.translation,
                    words: item.words.map((word) => ({
                        techFeature: word.tech_feature,
                        synonym: word.synonym,
                        synonymScore: word.synonym_score,
                        translation: word.translation,
                    })),
                };
            });
    }

    private _convertToPatentProfileWithMatchedTechFeature(data: RetrievalProgressFinalResult[]): PatentProfileWithMatchedTechFeature[] {
        const result: PatentProfileWithMatchedTechFeature[] = [];
        data.forEach((item) => {
            result.push({
                comparativeLiteratureType: item.comparative_literature_type,
                patentId: item.patent_id,
                mostSimilar: item.most_similar,
                matchedTechFeatures: item.features
                    .map((feature) => ({
                        techFeature: feature.tech_feature,
                        comparisonFeature: feature.comparison_feature,
                        score: feature.score,
                        publicScore: feature.public_score,
                        similar: feature.similar,
                        type: feature.type,
                        reason: feature.reason || '',
                        originalType: feature.original_type,
                        originalReason: feature.original_reason || '',
                    })),
                lang: item.lang,
                selected: item.selected,
                publicScore: item.public_score,
                extracted: item.features?.length > 0,
                userAdded: item.user_added,
            });
        });
        return result;
    }

    /**
     * 转换论文数据
     * @param data - 后端返回的论文数据
     * @returns 论文配置数据数组
     */
    private _convertToPaperProfileWithMatchedTechFeature(data: RetrievalProgressPaperResult[]): PaperProfileWithMatchedTechFeature[] {
        const result: PaperProfileWithMatchedTechFeature[] = [];
        const siteRegion = this._headerService.account?.siteRegion;
        data.forEach((item) => {
            // 兼容接口返回的 title/url 和 doc_title/doc_url 两种字段名
            const itemAny = item as RetrievalProgressPaperResult & { title?: string; url?: string };
            result.push({
                comparativeLiteratureType: item.comparative_literature_type,
                docId: item.doc_id,
                docTitle: item.doc_title || itemAny.title || '',
                docUrl: buildLiteratureUrlWithRegion(item.doc_id, siteRegion),
                mostSimilar: item.most_similar,
                matchedTechFeatures: item.features?.map((feature) => ({
                    techFeature: feature.tech_feature,
                    comparisonFeature: feature.comparison_feature,
                    score: feature.score,
                    publicScore: feature.public_score,
                    similar: feature.similar,
                    type: feature.type,
                    reason: feature.reason || '',
                    originalType: feature.original_type,
                    originalReason: feature.original_reason || '',
                })) || [],
                lang: item.lang,
                selected: item.selected,
                publicScore: item.public_score,
                extracted: item.features?.length > 0,
                userAdded: item.user_added,
                // 论文专用字段
                journal: item.journal,
                author: item.author,
                abstract: item.abstract,
                // 发表时间
                pbd: item.pbd,
            });
        });
        return result;
    }

    /**
     * 转换网站数据
     * @param data - 后端返回的网站数据
     * @returns 网站配置数据数组
     */
    private _convertToWebsiteProfileWithMatchedTechFeature(data: RetrievalProgressWebsiteResult[]): WebsiteProfileWithMatchedTechFeature[] {
        const result: WebsiteProfileWithMatchedTechFeature[] = [];
        data.forEach((item) => {
            // 兼容接口返回的 title/url 和 doc_title/doc_url 两种字段名
            const itemAny = item as RetrievalProgressWebsiteResult & { title?: string; url?: string };
            result.push({
                comparativeLiteratureType: item.comparative_literature_type,
                docId: item.doc_id,
                docTitle: item.doc_title || itemAny.title || '',
                docUrl: item.doc_url || itemAny.url,
                mostSimilar: item.most_similar,
                matchedTechFeatures: item.features?.map((feature) => ({
                    techFeature: feature.tech_feature,
                    comparisonFeature: feature.comparison_feature,
                    score: feature.score,
                    publicScore: feature.public_score,
                    similar: feature.similar,
                    type: feature.type,
                    reason: feature.reason || '',
                    originalType: feature.original_type,
                    originalReason: feature.original_reason || '',
                })) || [],
                lang: item.lang,
                selected: item.selected,
                publicScore: item.public_score,
                extracted: item.features?.length > 0,
                userAdded: item.user_added,
                // 网站专用字段
                source: item.source,
                // 发表时间
                pbd: item.pbd,
            });
        });
        return result;
    }

    private _getRetrievalTimeCost(data: {
        task_cost: number;
        excute_times: number;
        cc_docs_num: number;
    }): RetrievalTimeCost {
        return {
            timeSpend: data.task_cost,
            timeSaved: (data.excute_times + data.cc_docs_num) * HUMAN_RETRIEVAL_PER_PATENT_TIME,
        };
    }

    /**
     * 循环调用检索结果接口，直到检索完成
     */
    // eslint-disable-next-line complexity
    private async _loopSearchNoveltyResult(taskId: string, _callbacks: ComplexRetrievalCallbacks) {
        let isFinished = false;
        const deferred = create<void>();
        this._abortController = new AbortController();

        while (!isFinished) {
            const data = await this._searchNoveltyResultApi.getSearchNoveltyResult({
                task_id: taskId,
                is_finished: false,
            }, this._abortController.signal);
            _callbacks.onPatentFeatureMatched(this._convertToPatentProfileWithMatchedTechFeature(data.final_result));

            // 处理论文数据
            if (data.paper_result && data.paper_result.length > 0) {
                _callbacks.onPaperFeatureMatched(this._convertToPaperProfileWithMatchedTechFeature(data.paper_result));
            }

            // 处理网站数据
            if (data.website_result && data.website_result.length > 0) {
                _callbacks.onWebsiteFeatureMatched(this._convertToWebsiteProfileWithMatchedTechFeature(data.website_result));
            }

            _callbacks.onGetSearchElement(data.feature_analysis_result.keywords_extract_result, data.feature_analysis_result.family_merge_enabled || false);
            if (_.isNumber(data.task_cost) && _.isNumber(data.cc_docs_num) && _.isNumber(data.excute_times)) {
                _callbacks.onRetrievalTime(this._getRetrievalTimeCost(data));
            }
            this._retrievalProgressModel.update(() => {
                return data;
            });

            await sleep(SEARCH_DELAY);

            if (data.task_status !== TASK_STATUS.RUNNING) {
                isFinished = true;
                deferred.resolve();
            }
        }

        return deferred.promise;
    }

}
